import React, { useState, useCallback } from "react";
import { Card, Button, Row, Col, Table, Input, DatePicker, Select, Space, message } from "antd";
import { useNavigate } from "react-router-dom";
import { 
    SearchOutlined,
    ReloadOutlined
} from "@ant-design/icons";
import { useAuth } from "../../../hooks/useAuth";

const { RangePicker } = DatePicker;
const { Option } = Select;

const FlightCostWorkbenchPage = () => {
    // 导航功能
    const navigate = useNavigate();
    
    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();

    // 页面状态
    const [loading, setLoading] = useState(false);
    const [searchForm, setSearchForm] = useState({
        aircraftReg: [],
        routeFullPath: '',
        routeDate: null,
        dateRange: null,
        flightNumber: ''
    });


    // 模拟表格数据 - 包含所有费用类型字段
    const [tableData] = useState([
        {
            id: 1,
            aircraftReg: 'B-1234',
            route: 'HKG-PEK',
            routeDate: '2025-10-21',
            flightNumber: 'HU789',
            segment_sequence: null,
            status: 'completed',
            // 费用类型数据
            bh: null, // 用户手动录入
            fuel: 15420.50,
            navigation: 850.00,
            ground_handling: 1250.75,
            landing_parking: 2800.00,
            catering: 4500.25,
            transportation: null,
            traffic_rights_permit: 1200.00,
            positioning: null,
            uld: 650.50,
            sita: 120.00,
            deicing: null,
            accommodation: null,
            laundry: null,
            disinfection: 180.00,
            flight_service_charge: 950.75,
            others_fee: 320.50
        },
        {
            id: 2,
            aircraftReg: 'B-5678',
            route: 'PEK-BUD',
            routeDate: '2025-10-21', 
            flightNumber: 'HU790',
            segment_sequence: null,
            status: 'pending',
            // 费用类型数据
            bh: null,
            fuel: 12800.00,
            navigation: 720.00,
            ground_handling: 980.25,
            landing_parking: 2150.00,
            catering: null,
            transportation: 350.00,
            traffic_rights_permit: null,
            positioning: 800.00,
            uld: null,
            sita: 120.00,
            deicing: 450.75,
            accommodation: 1250.00,
            laundry: 85.50,
            disinfection: null,
            flight_service_charge: 750.00,
            others_fee: null
        },
        {
            id: 3,
            aircraftReg: 'B-9012',
            route: 'BUD-PEK',
            routeDate: '2025-10-22',
            flightNumber: 'HU791',
            segment_sequence: null,
            status: 'error',
            // 费用类型数据
            bh: null,
            fuel: null,
            navigation: 680.00,
            ground_handling: null,
            landing_parking: 1950.50,
            catering: 3200.00,
            transportation: null,
            traffic_rights_permit: 1100.00,
            positioning: null,
            uld: 420.25,
            sita: null,
            deicing: null,
            accommodation: null,
            laundry: null,
            disinfection: 160.00,
            flight_service_charge: null,
            others_fee: 180.75
        }
    ]);

    // 飞机号选项
    const [aircraftOptions] = useState(['B-1234', 'B-5678', 'B-9012', 'B-3456']);


    // 处理查询
    const handleQuery = useCallback(() => {
        setLoading(true);
        // 模拟API调用
        setTimeout(() => {
            setLoading(false);
            message.success('查询完成');
        }, 1000);
    }, []);

    // 处理重置
    const handleReset = useCallback(() => {
        setSearchForm({
            aircraftReg: [],
            routeFullPath: '',
            routeDate: null,
            dateRange: null,
            flightNumber: ''
        });
        message.success('重置完成');
    }, []);

    // 测试空数据状态
    const [showEmptyData, setShowEmptyData] = useState(false);
    
    // 获取显示数据
    const displayData = showEmptyData ? [] : tableData;

    // 定义费用类型和对应的显示名称 - 与航段费用管理页面保持一致
    const COST_TYPES = [
        { key: 'bh', title: 'BH', isEmpty: true }, // 用户手动录入，暂时空着
        { key: 'fuel', title: 'Fuel' },
        { key: 'navigation', title: 'Navigation' },
        { key: 'ground_handling', title: 'Ground Handling' },
        { key: 'landing_parking', title: 'Landing/Parking' },
        { key: 'catering', title: 'Catering' },
        { key: 'transportation', title: 'Transportation' },
        { key: 'traffic_rights_permit', title: 'Traffic Rights Permit' },
        { key: 'positioning', title: 'Positioning' },
        { key: 'uld', title: 'ULD' },
        { key: 'sita', title: 'SITA' },
        { key: 'deicing', title: 'Deicing' },
        { key: 'accommodation', title: 'Accommodation' },
        { key: 'laundry', title: 'Laundry' },
        { key: 'disinfection', title: 'Disinfection' },
        { key: 'flight_service_charge', title: 'Flight Service Charge' },
        { key: 'others_fee', title: 'Others' } // Others → OTHERS_FEE 映射正确
    ];

    // 渲染费用金额的通用方法
    const renderCostAmount = (value, record, costType) => {
        // 如果是标记为空的费用类型，不显示任何内容
        if (costType && costType.isEmpty) return null;
        
        if (!value || value === 0) return null; // 空值不显示
        
        const amount = parseFloat(value);
        return (
            <span style={{ 
                color: "#1890ff",
                fontWeight: "500",
                fontSize: "12px"
            }}>
                {amount.toFixed(2)}
            </span>
        );
    };

    // 表格列定义 - 与航段费用管理页面保持一致 (聚合视图格式)
    const columns = [
        // 固定左侧列
        {
            title: "执行日期",
            dataIndex: "routeDate",
            key: "routeDate", 
            width: 100,
            fixed: "left",
        },
        {
            title: "航班号",
            dataIndex: "flightNumber",
            key: "flightNumber",
            width: 80,
            fixed: "left",
        },
        {
            title: "航段",
            dataIndex: "route",
            key: "route",
            width: 100,
            fixed: "left",
        },
        {
            title: "飞机注册号",
            dataIndex: "aircraftReg",
            key: "aircraftReg",
            width: 120,
        },
        {
            title: "段次",
            dataIndex: "segment_sequence",
            key: "segment_sequence",
            width: 60,
            render: () => null, // 暂时空着
        },
        
        // 动态生成的费用类型列
        ...COST_TYPES.map(costType => {
            // 根据标题长度设置不同的列宽
            let columnWidth = 100; // 默认宽度
            if (costType.title.length > 12) {
                columnWidth = 140; // 长标题使用更宽的宽度
            } else if (costType.title.length > 8) {
                columnWidth = 120; // 中等长度标题使用中等宽度
            }

            return {
                title: costType.title,
                dataIndex: costType.key,
                key: costType.key,
                width: columnWidth,
                align: "right",
                render: (value, record) => {
                    // 特殊处理BH字段 - 暂时为空
                    if (costType.isEmpty) {
                        return null;
                    }
                    return renderCostAmount(value, record, costType);
                },
            };
        }),
        
        // 状态列
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 80,
            render: (status) => {
                const statusMap = {
                    completed: { color: '#52c41a', text: '已完成' },
                    pending: { color: '#faad14', text: '待处理' },
                    error: { color: '#ff4d4f', text: '异常' }
                };
                const statusInfo = statusMap[status] || { color: '#d9d9d9', text: '未知' };
                return (
                    <span style={{ color: statusInfo.color, fontWeight: 'bold' }}>
                        {statusInfo.text}
                    </span>
                );
            }
        },
        
        // 操作列
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 160,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            // 使用航班号作为taskId（实际应该是rotationId或taskId）
                            navigate(`/settlement/leg-detail/${record.flightNumber || record.id}`);
                        }}
                    >
                        详情
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            message.info('录入费用功能开发中...');
                        }}
                    >
                        录入费用
                    </Button>
                </Space>
            ),
        }
    ];

    return (
        <div className="page-container">
            {/* 页面头部 */}
            <div className="page-header-compact">
                <div className="page-title-compact">航班费用管理</div>
            </div>


            {/* 搜索表单 */}
            <Card className="filter-card" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col span={5}>
                        <div className="form-item">
                            <label>飞机注册号:</label>
                            <Select
                                mode="multiple"
                                placeholder="请选择飞机注册号"
                                value={searchForm.aircraftReg}
                                onChange={(value) => setSearchForm({...searchForm, aircraftReg: value})}
                                style={{ width: '100%' }}
                                allowClear
                            >
                                {aircraftOptions.map(reg => (
                                    <Option key={reg} value={reg}>{reg}</Option>
                                ))}
                            </Select>
                        </div>
                    </Col>
                    
                    <Col span={5}>
                        <div className="form-item">
                            <label>航段:</label>
                            <Input
                                placeholder="请输入航段"
                                value={searchForm.routeFullPath}
                                onChange={(e) => setSearchForm({...searchForm, routeFullPath: e.target.value})}
                                onPressEnter={handleQuery}
                            />
                        </div>
                    </Col>
                    
                    <Col span={4}>
                        <div className="form-item">
                            <label>执行日期:</label>
                            <DatePicker
                                placeholder="选择日期"
                                value={searchForm.routeDate}
                                onChange={(date) => setSearchForm({...searchForm, routeDate: date})}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </Col>
                    
                    <Col span={5}>
                        <div className="form-item">
                            <label>日期范围:</label>
                            <RangePicker
                                placeholder={['开始日期', '结束日期']}
                                value={searchForm.dateRange}
                                onChange={(dates) => setSearchForm({...searchForm, dateRange: dates})}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </Col>
                    
                    <Col span={5}>
                        <Space>
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />}
                                onClick={handleQuery}
                                loading={loading}
                            >
                                查询
                            </Button>
                            <Button 
                                icon={<ReloadOutlined />}
                                onClick={handleReset}
                            >
                                重置
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* 数据表格 */}
            <Card className="data-card">
                <div className="table-header">
                    <h3>航段费用数据</h3>
                </div>
                
                <Table
                    columns={columns}
                    dataSource={displayData || []}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: 1,
                        pageSize: 20,
                        total: (displayData || []).length,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                        pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    scroll={{ x: 3000 }}
                    size="middle"
                    locale={{
                        emptyText: '暂无数据'
                    }}
                    showHeader={true}
                />
            </Card>
        </div>
    );
};

export default FlightCostWorkbenchPage;
